import React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { IconClock, IconRefresh } from "@tabler/icons-react";
import MDialog from "@/components/ui/atoms/MDialog";
import { Reminders, ReminderType } from "@/types/reminders";
import { VITALS_PARAM_CONFIG } from "./vitals/vitalsRemindersHelper";
import { getEmphasizedText } from "./remindersHelper";
import { formatTimeWithTimezone } from "@/utils/date-utils";
import styles from "./ReminderSummary.module.css";

export interface ReminderViewModalProps {
  reminder: Reminders | null;
  // Generic per-type icon (same one shown on the listing card) — used as
  // the recap icon for every type except Vitals, which keeps its own
  // parameter-specific gradient/icon below.
  icon?: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

// Read-only "view" modal shown when tapping a reminder on the listing
// screen (Figma 5257-59552) — recap card, date, dose/reminder times, and
// notes, all display-only. Editing is a separate action (the listing's
// existing 3-dot "Edit" menu), which opens the full edit screen instead.
const ReminderViewModal: React.FC<ReminderViewModalProps> = ({
  reminder,
  icon,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!reminder) return null;

  const vitalsCfg =
    reminder.type === ReminderType.VITALS && reminder.vitalParameter
      ? VITALS_PARAM_CONFIG[reminder.vitalParameter]
      : null;

  // Same type-aware summary line shown on the listing card itself, so the
  // modal's subtitle is never blank/wrong for non-Vitals reminder types.
  const frequencyText = getEmphasizedText(reminder, t);

  // Multiple-times-a-day reminders (medication, vitals) carry their times in
  // dayFrequencyValue; everything else fires once, at reminder.startDate —
  // fall back to that single time so every type gets a "Reminder time"
  // section instead of just the ones with an explicit dose schedule.
  const timeSlots =
    reminder.dayFrequencyValue && reminder.dayFrequencyValue.length > 0
      ? reminder.dayFrequencyValue
      : reminder.startDate
      ? [formatTimeWithTimezone(reminder.startDate, reminder.timezone)]
      : [];

  const title = reminder.name
    ? t("reminders.viewModal.titleFor", { name: reminder.name })
    : t("reminders.viewModal.titleOwn");

  return (
    <MDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      showPrimaryCta={false}
    >
      <div className="flex flex-col gap-6 w-full">
        <div className={styles.recapCard}>
          {vitalsCfg ? (
            <div className={`${styles.recapIcon} ${vitalsCfg.iconClass}`}>
              <vitalsCfg.Icon size={24} color="#45168C" />
            </div>
          ) : (
            icon && (
              <div className={`${styles.recapIcon} ${styles.recapIconDefault}`}>
                {icon}
              </div>
            )
          )}
          <div className={styles.recapText}>
            <span className={styles.recapLabel}>{reminder.reminderFor}</span>
            {frequencyText && (
              <span className={styles.recapFrequency}>
                <IconRefresh size={16} className={styles.recapFrequencyIcon} />
                {frequencyText}
              </span>
            )}
          </div>
        </div>

        {reminder.startDate && (
          <div className="flex flex-col gap-2 w-full">
            <p className="text-[12px] font-medium text-[#7046AE] tracking-[0.12px] m-0">
              {t("reminders.viewModal.reminderDateLabel")}
            </p>
            <p className="text-[16px] font-medium text-[#36116D] m-0">
              {format(new Date(reminder.startDate), "EEE, dd MMM yyyy")}
            </p>
          </div>
        )}

        {timeSlots.length > 0 && (
          <div className="flex flex-col gap-3.5 w-full">
            <p className="text-[12px] font-medium text-[#7046AE] tracking-[0.12px] m-0">
              {t("reminders.viewModal.doseTimesLabel")}
            </p>
            <div className={styles.timeSlotBox}>
              <div className={styles.timeSlotRow}>
                {timeSlots.map((time, index) => (
                  <div
                    key={`${time}-${index}`}
                    className={`${styles.timeChip} ${styles.timeChipReadOnly}`}
                  >
                    <span className={styles.timeChipBadge}>{index + 1}</span>
                    <span className={styles.timeChipValue}>{time}</span>
                    <IconClock size={16} className={styles.timeChipClock} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {reminder.notes && (
          <div className="flex flex-col gap-3 w-full">
            <p className="text-[12px] font-medium text-[#7046AE] tracking-[0.12px] m-0">
              {t("reminders.viewModal.detailsLabel")}
            </p>
            <div className="border border-[#C8B7E0] rounded-xl px-1.5 py-[15px] w-full box-border">
              <p className="text-[12px] font-medium text-[#36116D] tracking-[0.12px] m-0">
                {reminder.notes}
              </p>
            </div>
          </div>
        )}
      </div>
    </MDialog>
  );
};

export default ReminderViewModal;
